import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    IconButton,
    ButtonGroup,
    Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import softhouseLogo from 'assets/images/softhouseLogo.png';
import { isAdmin } from 'utils/auth';
import { CHATBOT_ASK_URL } from 'api/chatbot';
import { EMPLOYEE_MANUAL_ASK } from 'api/employeeManual';
import useChatbotSession from 'hooks/useChatbotSession';
import ChatMessages from './ChatMessages';
import ChatInput from './ChatInput';
import PropTypes from 'prop-types';
import { theme } from 'utils/theme';

const Chatbot = ({ open, onClose }) => {
    const [selectedTab, setSelectedTab] = useState(0);
    const messagesEndRef = useRef(null);

    const manualChat = useChatbotSession(
        EMPLOYEE_MANUAL_ASK,
        'Hello! How can I help you with the employee manual today?'
    );
    const dataChat = useChatbotSession(
        CHATBOT_ASK_URL,
        'Hello! How can I help you with your data today?'
    );

    const currentChat = selectedTab === 1 ? dataChat : manualChat;
    const { messages, sendMessage, loading } = currentChat;

    const handleSend = useCallback(
        async (message) => {
            await sendMessage(message);
        },
        [sendMessage]
    );

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, open]);

    if (!open) {
        return null;
    }

    return (
        <Paper
            elevation={8}
            sx={{
                position: 'fixed',
                bottom: 150,
                right: 24,
                width: 380,
                height: 550,
                display: 'flex',
                flexDirection: 'column',
                zIndex: 1300,
                borderRadius: 2,
                overflow: 'hidden',
                backgroundColor: theme.palette.grey[100],
            }}
        >
            <Box
                sx={{
                    p: '14px',
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <img src={softhouseLogo} alt="Logo" style={{ width: 34, height: 34 }} />
                    <Typography variant="h6" sx={{ marginLeft: '8px' }}>
                        Softhouser
                    </Typography>
                </Box>
                <IconButton
                    size="small"
                    onClick={onClose}
                    sx={{ color: 'primary.contrastText' }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>
            {isAdmin() && (
                <>
                    <ButtonGroup variant="outlined" sx={{ marginY: '12px', marginX: 'auto', backgroundColor: theme.palette.whiteColor.main }}>
                        <Button
                            variant={selectedTab === 0 ? 'contained' : 'outlined'}
                            onClick={() => setSelectedTab(0)}
                            sx={{ width: 150 }}
                        >
                            Employee manual
                        </Button>
                        <Button
                            variant={selectedTab === 1 ? 'contained' : 'outlined'}
                            onClick={() => setSelectedTab(1)}
                            sx={{ width: 150 }}
                        >
                            Data
                        </Button>
                    </ButtonGroup>
                    <Divider />
                </>
            )}
            <ChatMessages
                messages={messages}
                isLoading={loading}
                endRef={messagesEndRef}
            />
            <ChatInput
                onSend={handleSend}
                disabled={loading}
            />
        </Paper>
    );
};

Chatbot.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func
};

export default Chatbot;